import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { v4 as uuidv4 } from 'uuid';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { Spinner } from 'libs.nucleus';
import { ConsentElementOverlay } from './consent_element_overlay.component';
import { ConsentElementToolbar } from './consent_element_toolbar.component';
import { CONSENT_ELEMENT_TYPES } from '../../constants/consent_element_types.constants';
import { useConsentContext } from '../../hooks/use_consent_context.hook';
import { ConsentElementConfigModal } from '../element_config/consent_element_config_modal.component';
// TODO: Use a string URL pointing to the local node_modules copy
const BASE_URL = `//unpkg.com/pdfjs-dist@${pdfjs.version}`;
pdfjs.GlobalWorkerOptions.workerSrc = `${BASE_URL}/build/pdf.worker.min.mjs`;
const pdfOptions = {
    cMapUrl: `https:${BASE_URL}/cmaps/`,
    standardFontDataUrl: `https:${BASE_URL}/standard_fonts/`,
};
export const ConsentPdfEditorContent = ({ document }) => {
    const { addElement, updateElement, deleteElement, updateDocument } = useConsentContext();
    const [numPages, setNumPages] = useState(document.totalPages || 0);
    const [activePlacementType, setActivePlacementType] = useState(null);
    const [configuringElement, setConfiguringElement] = useState(null);
    const onDocumentLoadSuccess = useCallback(({ numPages: pages }) => {
        setNumPages(pages);
        if (document.totalPages !== pages) {
            void updateDocument({ ...document, totalPages: pages });
        }
    }, [document, updateDocument]);
    const handlePlaceElement = (pageNumber, e) => {
        if (!activePlacementType) {
            return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        const def = CONSENT_ELEMENT_TYPES[activePlacementType];
        const xPct = Math.max(0, Math.min(1 - def.defaultWidth, (e.clientX - rect.left) / rect.width));
        const yPct = Math.max(0, Math.min(1 - def.defaultHeight, (e.clientY - rect.top) / rect.height));
        const newElement = {
            id: `el-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            type: activePlacementType,
            page: pageNumber,
            position: { xPct, yPct, widthPct: def.defaultWidth, heightPct: def.defaultHeight },
            config: { ec__key: uuidv4() },
        };
        addElement(document.id, newElement);
        setActivePlacementType(null);
    };
    const handleUpdateElement = (element) => {
        updateElement(document.id, element);
    };
    const handleDeleteElement = (elementId) => {
        deleteElement(document.id, elementId);
    };
    const handleConfigureSave = (element) => {
        updateElement(document.id, element);
        setConfiguringElement(null);
    };
    return (_jsxs("div", { className: 'flex min-h-0 flex-1 flex-col', "data-testid": 'consent-pdf-editor-content', children: [_jsxs("div", { className: 'flex min-h-0 flex-1 flex-col bg-[#f3f4f6]', children: [_jsx("div", { className: 'sticky top-0 z-20 shrink-0 py-2 px-4', children: _jsx(ConsentElementToolbar, { activePlacementType: activePlacementType, onSelectType: setActivePlacementType, onCancelPlacement: () => setActivePlacementType(null) }) }), _jsx("div", { className: 'min-h-0 flex-1 overflow-auto p-4', "data-testid": 'pdf-document-wrapper', children: _jsx(Document, { file: document.pdfDataUrl, onLoadSuccess: onDocumentLoadSuccess, options: pdfOptions, loading: _jsx(Spinner, { wrapper: 'full' }), "data-testid": 'pdf-document', children: _jsx("div", { className: 'flex flex-col gap-4', children: Array.from({ length: numPages }, (_, i) => {
                                    const pageNumber = i + 1;
                                    return (_jsxs("div", { className: 'relative mx-auto', "data-testid": `pdf-page-wrapper-${pageNumber}`, style: { cursor: activePlacementType ? 'crosshair' : 'default' }, onClick: (e) => handlePlaceElement(pageNumber, e), children: [_jsx(Page, { pageNumber: pageNumber, scale: 1, loading: _jsx(Spinner, {}) }), _jsx(ConsentElementOverlay, { pageNumber: pageNumber, elements: document.elements, onUpdateElement: handleUpdateElement, onDeleteElement: handleDeleteElement, onConfigureElement: setConfiguringElement })] }, pageNumber));
                                }) }) }) })] }), _jsx(ConsentElementConfigModal, { element: configuringElement, isOpen: !!configuringElement, onClose: () => setConfiguringElement(null), onSave: handleConfigureSave })] }));
};
